// Advisory drill-down — item 141 Phase B. Header with severity +
// external references, plus the list of artifacts in this platform
// that match the advisory (empty state when none do).

import type { KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertOctagon, ArrowLeft, ExternalLink } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAdvisory } from '../../api/advisories';

function severityTone(s: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'CRITICAL':
    case 'HIGH':
      return 'danger';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'success';
    default:
      return 'neutral';
  }
}

interface AdvisoryReference {
  type?: string;
  url: string;
}

export function AdvisoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: advisory, isLoading } = useAdvisory(id);
  const references = Array.isArray(advisory?.references)
    ? (advisory.references as AdvisoryReference[])
    : [];

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, artifactID: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/artifacts/${artifactID}`);
    }
  };

  return (
    <SecurityShell
      eyebrow="Intelligence · advisories"
      title={advisory ? advisory.advisory_id : 'Advisory'}
      description="Advisory metadata and every artifact in this platform matched against it."
    >
      <Link
        to="/security/advisories"
        className="inline-flex items-center gap-2 text-fg-muted hover:text-fg text-sm"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to advisories
      </Link>

      {isLoading ? (
        <Card>
          <CardBody>
            <p className="text-fg-muted">Loading…</p>
          </CardBody>
        </Card>
      ) : !advisory ? (
        <Card>
          <CardBody>
            <p className="text-fg-muted">Advisory not found.</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <CardBody className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {advisory.severity && (
                  <Badge tone={severityTone(advisory.severity)}>{advisory.severity}</Badge>
                )}
                <span className="orb-code text-xs">{advisory.advisory_id}</span>
                <span className="text-xs text-fg-muted">{advisory.ecosystem}</span>
                <span className="text-xs text-fg-muted">·</span>
                <span className="text-sm font-mono text-fg">{advisory.package_name}</span>
                <Badge tone="neutral">{advisory.source}</Badge>
              </div>
              <p className="text-sm text-fg-muted">{advisory.summary}</p>
              {references.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-1">
                  {references.map((ref) => (
                    <a
                      key={ref.url}
                      href={ref.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {ref.type || 'reference'}
                    </a>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={`Affected packages (${advisory.matches.length})`}
              description="Artifacts in this platform matched against the advisory's affected ranges."
            />
            {advisory.matches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-5 py-3 font-medium text-fg-muted">Project / repo</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Path</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Version</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">Format</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advisory.matches.map((m) => (
                      <tr
                        key={m.id}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(`/artifacts/${m.id}`)}
                        onKeyDown={(e) => onRowKeyDown(e, m.id)}
                        className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer focus:outline-none focus:bg-bg-hover/40"
                      >
                        <td className="px-5 py-3 text-accent">
                          {m.project_slug}/{m.repo_slug}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">{m.path}</td>
                        <td className="px-5 py-3">
                          {m.version ? <span className="orb-code text-xs">{m.version}</span> : '—'}
                        </td>
                        <td className="px-5 py-3 text-fg-muted text-xs">{m.format}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <CardBody>
                <p className="text-sm text-fg-muted flex items-center gap-2">
                  <AlertOctagon className="h-4 w-4 text-fg-subtle" />
                  No matched artifacts in this platform.
                </p>
              </CardBody>
            )}
          </Card>
        </>
      )}
    </SecurityShell>
  );
}
